import { ArrowLeft, ArrowRight } from "lucide-react";
import React from "react";
import * as S from "./styled";
import usePagination from "./usePagination";

type TButtonName = "previous" | "next";
type TGetButtonLabel = (name: TButtonName) => string;

type TPaginationProps = {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    siblingsCount?: number;
    trackName?: string;
    trackData?: object;
    getButtonLabel?: TGetButtonLabel;
};

const getDefaultButtonLabel: TGetButtonLabel = name => `${name.charAt(0).toUpperCase()}${name.slice(1)}`;

const Pagination = ({
    currentPage,
    totalPages,
    siblingsCount = 2,
    onPageChange,
    getButtonLabel = getDefaultButtonLabel
}: TPaginationProps) => {
    const pagination = usePagination(currentPage, totalPages, siblingsCount);

    const handleLeftPage = () => {
        if (currentPage > 1) {
            onPageChange(currentPage - 1);
        }
    };

    const handleRightPage = () => {
        if (currentPage < totalPages) {
            onPageChange(currentPage + 1);
        }
    };

    return (
        <S.Pagination>
            <S.PaginationControl
                onClick={handleLeftPage}
                disabled={currentPage === 1}
                trackName="previous"
                trackData={{ currentPage }}
                title={getButtonLabel("previous")}
                variant="secondary"
                icon={<ArrowLeft />}
            />
            <S.PaginationNumbersWrapper>
                {pagination.map(item => {
                    if (typeof item !== "number") return <S.PaginationEllipsis key={item}>…</S.PaginationEllipsis>;

                    return (
                        <S.PaginationItem
                            key={item}
                            title={item.toString()}
                            active={item === currentPage}
                            onClick={() => onPageChange(item)}
                            trackName="page"
                            trackData={{ nextPage: item }}
                            variant="tertiary"
                        />
                    );
                })}
            </S.PaginationNumbersWrapper>
            <S.PaginationControl
                onClick={handleRightPage}
                disabled={currentPage === totalPages}
                trackName="next"
                title={getButtonLabel("next")}
                endIcon={<ArrowRight />}
                variant="secondary"
            />
        </S.Pagination>
    );
};

export default Pagination;
